/**
 * FieldDayPill — the office nav entry point into a dual-access user's own
 * field schedule.
 *
 * Lives in its own module, apart from FieldDayModal, for one reason: the modal
 * statically imports `TechJobDetailV6` and `TechJobCardV2`, which reach most of
 * `src/components/tech` — 864 kB that landed in the office ENTRY chunk purely
 * because the always-rendered pill shared a file with the modal. Splitting the
 * two lets the modal be lazy while the pill stays cheap and immediate.
 *
 * Keep it that way: do not import anything from `components/tech` here.
 */

import { useMemo } from 'react';
import { Briefcase } from 'lucide-react';
import { useJobsByTech } from '@/hooks/useJobs';
import { Job } from '@/types/schema';

interface FieldDayPillProps {
  userId: string;
  onClick: () => void;
  /**
   * When `true`, the pill renders even with zero upcoming jobs (shows "My Field"
   * with no badge) — used for dual-access users who should always have a way
   * into their tech schedule. When `false` (legacy behaviour), the pill is
   * hidden when no deployments are scheduled.
   */
  alwaysVisible?: boolean;
}

/** YYYY-MM-DD in local timezone — never toISOString(), which is yesterday in AEST. */
function toLocalDateStr(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/**
 * Count jobs assigned to `userId` scheduled for today or tomorrow.
 * Used by FieldDayPill to render the count badge.
 */
function useUpcomingDeploymentCount(userId: string): number {
  const { jobs } = useJobsByTech(userId);
  return useMemo(() => {
    if (!jobs || jobs.length === 0) return 0;
    const today = new Date();
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);
    const windowSet = new Set([toLocalDateStr(today), toLocalDateStr(tomorrow)]);
    return jobs.filter((j: Job) => windowSet.has(j.date)).length;
  }, [jobs]);
}

/**
 * Pill button for the office nav bar. Always renders a count badge — when
 * `alwaysVisible` is set, the pill stays purple and clickable even with zero
 * upcoming deployments, with a "0" badge so dual-access users can clearly see
 * the entry point ("nothing scheduled today, but I can still browse my week").
 */
export function FieldDayPill({ userId, onClick, alwaysVisible = false }: FieldDayPillProps) {
  const count = useUpcomingDeploymentCount(userId);
  if (count === 0 && !alwaysVisible) return null;

  const hasDeployments = count > 0;

  return (
    <button
      onClick={onClick}
      className={`relative flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-semibold transition-colors ${
        hasDeployments
          ? 'bg-purple-500/20 text-purple-500 hover:bg-purple-500/30'
          : 'bg-purple-500/10 text-purple-500/80 hover:bg-purple-500/20'
      }`}
      title={
        hasDeployments
          ? `You're deployed — ${count} job${count === 1 ? '' : 's'} in the next 2 days`
          : 'Open your field schedule — no jobs scheduled today or tomorrow'
      }
    >
      <Briefcase className="w-3.5 h-3.5" />
      <span className="hidden sm:inline">Field Day</span>
      <span
        className={`text-[10px] font-bold rounded-full min-w-[18px] h-[18px] px-1 flex items-center justify-center ${
          hasDeployments ? 'bg-purple-500 text-white' : 'bg-purple-500/40 text-white'
        }`}
      >
        {count}
      </span>
    </button>
  );
}
